import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiSearch, FiArrowUpRight } from "react-icons/fi";
import Tilt from "../cardItems/Tilt";
import products from "../../Categorie";
import { hasImage, normalize } from "./catalogueData";
import "./Catalogue.css";

const ALL = "all";

const ProductListWithCategories = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState(ALL);

  const tiles = useMemo(() => {
    const query = normalize(searchTerm);
    return products
      .filter((c) => activeCategory === ALL || c.code === activeCategory)
      .flatMap((category) =>
        category.souscategories
          .filter((sub) => normalize(sub.nom).includes(query))
          .map((sub) => ({ category, sub }))
      );
  }, [searchTerm, activeCategory]);

  return (
    <div className="cat">
      <header className="cat-hero">
        <motion.span
          className="cat-eyebrow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Catalogue
        </motion.span>
        <motion.h1
          className="cat-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          Nos réalisations en inox
        </motion.h1>
        <motion.p
          className="cat-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        >
          Mobilier, menuiserie et équipements industriels, fabriqués sur mesure.
        </motion.p>
        <motion.label
          className="cat-search"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            placeholder="Rechercher une catégorie (chaises, portes, tuyauterie...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </motion.label>
      </header>

      <div className="cat-chips">
        <button
          type="button"
          className={`cat-chip${activeCategory === ALL ? " is-active" : ""}`}
          onClick={() => setActiveCategory(ALL)}
        >
          Tout
        </button>
        {products.map((category) => (
          <button
            type="button"
            key={category.code}
            className={`cat-chip${
              activeCategory === category.code ? " is-active" : ""
            }`}
            onClick={() => setActiveCategory(category.code)}
          >
            {category.nom}
          </button>
        ))}
      </div>

      <main className="cat-main">
        <motion.div layout className="cat-grid">
          <AnimatePresence mode="popLayout">
            {tiles.map(({ category, sub }) => (
              <motion.div
                layout
                key={sub.idcode}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.35 }}
              >
                <Tilt className="cat-tile-tilt">
                  <Link to={`/catalogue/${sub.idcode}`} className="cat-tile">
                    {hasImage(sub.image) && (
                      <img
                        className="cat-tile__img"
                        src={sub.image}
                        alt={sub.nom.trim()}
                        loading="lazy"
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    )}
                    <div className="cat-tile__shade" />
                    <span className="cat-tile__tag">{category.nom}</span>
                    <div className="cat-tile__body">
                      <h3>{sub.nom.trim()}</h3>
                      {sub.description && <p>{sub.description}</p>}
                      <span className="cat-tile__count">
                        {(sub.produits || []).length} produit
                        {(sub.produits || []).length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <span className="cat-tile__arrow">
                      <FiArrowUpRight />
                    </span>
                  </Link>
                </Tilt>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {tiles.length === 0 && (
          <p className="cat-empty">
            Aucune catégorie ne correspond à « {searchTerm} ».
          </p>
        )}
      </main>
    </div>
  );
};

export default ProductListWithCategories;
